import { Box, Button, Container, Header, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import ServiceCollection from 'src/services/ServiceCollection';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { buildInitialState } from 'src/services/accounting-calendar/AccountingCalendarState';
import { ACTIONS, accountingCalendarReducer } from 'src/services/accounting-calendar/AccountingCalendarReducer';
import CustomTag from 'src/models/accounting-calendar/CustomTag';
import { CalendarDay } from 'src/models/accounting-calendar/GetCalendarResult';
import { DeleteScope } from 'src/models/accounting-calendar/CustomTagEnums';
import ErrorUtils from 'src/utils/ErrorUtils';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import AccountingCalendarGrid from './AccountingCalendarGrid';
import CustomTagForm from './CustomTagForm';
import TagDetailsModal from './TagDetailsModal';
import DayTagsModal from './DayTagsModal';
import DeleteTagDialog from './DeleteTagDialog';
import { deriveMecDayLabel, accountingTodayIso, MONTH_NAMES } from './mecDayLabel';
import './AccountingCalendar.scss';

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

export default function AccountingCalendarView(props: { services: ServiceCollection }) {
    const { services } = props;
    // Anchor "today" (and the initial month/year) to the accounting timezone (Pacific), matching
    // the MEC-day label — so the highlighted cell and the label never disagree across timezones.
    const todayIso = accountingTodayIso();
    const currentYear = Number(todayIso.slice(0, 4));
    const currentMonth = Number(todayIso.slice(5, 7));

    const [state, dispatch] = useReducerWithLogger(
        accountingCalendarReducer,
        buildInitialState(currentMonth, currentYear),
    );
    const { viewMonth, viewYear, selectedTag, formMode, deleteDialogOpen, reloadKey } = state;

    const [calendar, loading, loadError] = services.accountingCalendarService.getCalendar(String(viewYear), reloadKey);
    const [mutationError, setMutationError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [viewMoreDate, setViewMoreDate] = useState<string | null>(null);
    const [viewMoreTags, setViewMoreTags] = useState<CustomTag[]>([]);

    const monthKey = `${viewYear}-${pad(viewMonth)}`;
    const monthsMap = (calendar && calendar.months) || {};
    const monthDays: CalendarDay[] = monthsMap[monthKey] || [];
    // Include adjacent months so the grid's leading/trailing spillover cells (which belong to the
    // previous/next calendar month but sit in this month's close window) show their day labels and
    // tags too. Adjacent months outside the fetched year (Dec of prior year / Jan of next) simply
    // have no data and render blank.
    const prevMonthDays: CalendarDay[] = viewMonth > 1 ? (monthsMap[`${viewYear}-${pad(viewMonth - 1)}`] || []) : [];
    const nextMonthDays: CalendarDay[] = viewMonth < 12 ? (monthsMap[`${viewYear}-${pad(viewMonth + 1)}`] || []) : [];
    const gridDays: CalendarDay[] = [...prevMonthDays, ...monthDays, ...nextMonthDays];

    // Header label: today's MEC day, derived from the calendar response.
    const headerLabel = deriveMecDayLabel(calendar);

    async function handleCreateOrUpdate(tag: CustomTag) {
        try {
            setSubmitting(true);
            setMutationError('');
            if (formMode === 'edit' && selectedTag && selectedTag.tagId) {
                // Carry the server-managed tagId onto the edited tag. NOTE: how a one-time tag's
                // date change (which moves its DynamoDB partition) is handled is a backend-contract
                // decision — UpdateCustomTag has no Java impl yet, so this sends the edited tag as-is.
                const updatedTag: CustomTag = { ...tag, tagId: selectedTag.tagId };
                await services.accountingCalendarService.updateTag(updatedTag);
            } else {
                await services.accountingCalendarService.createTag(tag);
            }
            dispatch(ACTIONS.BUMP_RELOAD.withPayload(null));
        } catch (ex) {
            setMutationError(ErrorUtils.getMessage(ex));
        } finally {
            setSubmitting(false);
        }
    }

    async function handleDelete(scope: DeleteScope, targetMonth?: string) {
        if (!selectedTag) {
            return;
        }
        try {
            setSubmitting(true);
            setMutationError('');
            await services.accountingCalendarService.deleteTag(selectedTag, scope, targetMonth);
            dispatch(ACTIONS.BUMP_RELOAD.withPayload(null));
        } catch (ex) {
            setMutationError(ErrorUtils.getMessage(ex));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    description={headerLabel}
                    actions={
                        <Button
                            variant="primary"
                            className="scheduleActivityButton"
                            onClick={() => { setMutationError(''); dispatch(ACTIONS.OPEN_CREATE_FORM.withPayload(null)); }}
                        >
                            Schedule activity
                        </Button>
                    }
                >
                    MEC calendar
                </Header>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <div className="acctCalendarMonthNav">
                    <Button
                        variant="icon"
                        iconName="angle-left"
                        ariaLabel="Previous month"
                        disabled={viewMonth === 1}
                        onClick={() => dispatch(ACTIONS.GO_TO_PREV_MONTH.withPayload(null))}
                    />
                    <Box variant="h3" data-testid="monthLabel">{MONTH_NAMES[viewMonth - 1]} {viewYear}</Box>
                    <Button
                        variant="icon"
                        iconName="angle-right"
                        ariaLabel="Next month"
                        disabled={viewMonth === 12}
                        onClick={() => dispatch(ACTIONS.GO_TO_NEXT_MONTH.withPayload(null))}
                    />
                </div>

                {loadError && (
                    <Box color="text-status-error" data-testid="calendarError">{loadError}</Box>
                )}

                <TPELoadingSpinner loading={loading} loadingText="Loading calendar...">
                    <AccountingCalendarGrid
                        year={viewYear}
                        month={viewMonth}
                        days={gridDays}
                        todayIso={todayIso}
                        onSelectTag={(tag) => dispatch(ACTIONS.SELECT_TAG.withPayload(tag))}
                        onViewMore={(dateIso, tags) => {
                            setViewMoreDate(dateIso);
                            setViewMoreTags(tags);
                        }}
                    />
                </TPELoadingSpinner>
            </SpaceBetween>

            {selectedTag && formMode === 'closed' && !deleteDialogOpen && (
                <TagDetailsModal
                    tag={selectedTag}
                    onEdit={() => { setMutationError(''); dispatch(ACTIONS.OPEN_EDIT_FORM.withPayload(selectedTag)); }}
                    onDelete={() => { setMutationError(''); dispatch(ACTIONS.OPEN_DELETE_DIALOG.withPayload(selectedTag)); }}
                    onClose={() => dispatch(ACTIONS.CLEAR_SELECTED_TAG.withPayload(null))}
                />
            )}

            {viewMoreDate && !selectedTag && (
                <DayTagsModal
                    dateLabel={viewMoreDate}
                    tags={viewMoreTags}
                    onSelectTag={(tag) => {
                        setViewMoreDate(null);
                        setViewMoreTags([]);
                        dispatch(ACTIONS.SELECT_TAG.withPayload(tag));
                    }}
                    onClose={() => {
                        setViewMoreDate(null);
                        setViewMoreTags([]);
                    }}
                />
            )}

            {(formMode === 'create' || formMode === 'edit') && (
                <CustomTagForm
                    mode={formMode}
                    initialTag={formMode === 'edit' ? selectedTag : null}
                    submitting={submitting}
                    submitError={mutationError}
                    onSubmit={handleCreateOrUpdate}
                    onClose={() => dispatch(ACTIONS.CLOSE_FORM.withPayload(null))}
                />
            )}

            {deleteDialogOpen && selectedTag && (
                <DeleteTagDialog
                    tag={selectedTag}
                    deleting={submitting}
                    deleteError={mutationError}
                    onConfirm={handleDelete}
                    onClose={() => dispatch(ACTIONS.CLOSE_DELETE_DIALOG.withPayload(null))}
                />
            )}
        </Container>
    );
}
